import { useCallback, useState } from 'react';

// 입력칸 오류 상태. 규격: docs/superpowers/specs/2026-09-14-ops-auth-toss.md D7.
//
// 회원가입이 입력칸에 관한 안내를 alert() 대신 칸 아래 한 줄로 보이려고 쓴다.
// 오류를 받을 수 있는 칸(또는 묶음 상자)에는 data-field 표지를 붙인다 — 제출 시
// 첫 오류 칸을 화면 순서로 찾는다. 객체 키 순서는 검사 코드의 순서일 뿐이라 믿지 않는다.
const FOCUSABLE = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])';

export default function useFieldErrors() {
    const [errors, setErrors] = useState({});

    const replaceErrors = useCallback((next) => setErrors(next || {}), []);

    const setError = useCallback((field, message) => {
        setErrors((prev) => ({ ...prev, [field]: message }));
    }, []);

    const clearError = useCallback((field) => {
        setErrors((prev) => {
            if (!(field in prev)) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    const fieldProps = useCallback((field) => ({
        'data-field': field,
        'aria-invalid': errors[field] ? 'true' : undefined,
        'aria-describedby': errors[field] ? `${field}-error` : undefined
    }), [errors]);

    const focusFirstError = useCallback((next) => {
        const keys = Object.keys(next || {});
        if (keys.length === 0 || typeof document === 'undefined') return;
        const marked = Array.from(document.querySelectorAll('[data-field]'));
        const target = marked.find((el) => keys.includes(el.getAttribute('data-field')));
        if (!target) return;
        const focusable = target.matches(FOCUSABLE) ? target : target.querySelector(FOCUSABLE);
        if (target.scrollIntoView) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (focusable) focusable.focus({ preventScroll: true });
    }, []);

    return { errors, replaceErrors, setError, clearError, fieldProps, focusFirstError };
}
